import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getVolume, speakText, setReplay, setHud } from './lzAudio';
import { createPortal } from 'react-dom';

// Age-progressive jigsaw puzzle (ages 3-5). Self-contained: no external
// images, no libraries beyond React. Pieces are slices of one SVG data URL
// clipped with background-position.

const BOARD = 240;
const SNAP_DISTANCE = 55;
const STORAGE_KEY = 'syntch_puzzle_completed';
const PHRASES = ["Amazing work!", "You're a star!", "So clever!", "Brilliant!", "Wonderful!"];

// ─── SVG drawings (viewBox 0 0 240 240) ────────────────────────────────────
const wrap = (inner) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="240" height="240">${inner}</svg>`;

const drawSun = () => {
    let rays = '';
    for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        const len = i % 2 === 0 ? 96 : 68;
        const x1 = 120 + Math.cos(a) * 62, y1 = 120 + Math.sin(a) * 62;
        const x2 = 120 + Math.cos(a) * len, y2 = 120 + Math.sin(a) * len;
        rays += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#FF8C00" stroke-width="14" stroke-linecap="round"/>`;
    }
    return wrap(`
    <rect width="240" height="240" fill="#87CEEB"/>
    <rect y="160" width="240" height="80" fill="#90EE90"/>
    ${rays}
    <circle cx="120" cy="120" r="55" fill="#FFD700"/>
    <path d="M92 96 Q102 88 112 96" stroke="#E67E00" stroke-width="4" fill="none" stroke-linecap="round"/>
    <path d="M128 96 Q138 88 148 96" stroke="#E67E00" stroke-width="4" fill="none" stroke-linecap="round"/>
    <circle cx="102" cy="110" r="7" fill="#000"/><circle cx="138" cy="110" r="7" fill="#000"/>
    <circle cx="104" cy="108" r="2.5" fill="#fff"/><circle cx="140" cy="108" r="2.5" fill="#fff"/>
    <path d="M96 132 Q120 156 144 132" stroke="#000" stroke-width="4" fill="none" stroke-linecap="round"/>`);
};

const drawFlower = () => {
    let petals = '';
    for (let i = 0; i < 8; i++) {
        petals += `<ellipse cx="120" cy="62" rx="18" ry="30" fill="${i % 2 ? '#FF1493' : '#FF69B4'}" transform="rotate(${i * 45} 120 100)"/>`;
    }
    let dots = '';
    for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        dots += `<circle cx="${120 + Math.cos(a) * 22}" cy="${100 + Math.sin(a) * 22}" r="2.5" fill="#FF8C00"/>`;
    }
    return wrap(`
    <rect width="240" height="240" fill="#E8F5E9"/>
    <rect y="170" width="240" height="70" fill="#66BB6A"/>
    <rect x="114" y="120" width="12" height="100" rx="5" fill="#2E7D32"/>
    <ellipse cx="94" cy="170" rx="24" ry="11" fill="#43A047" transform="rotate(-30 94 170)"/>
    <ellipse cx="146" cy="160" rx="24" ry="11" fill="#43A047" transform="rotate(30 146 160)"/>
    ${petals}
    <circle cx="120" cy="100" r="28" fill="#FFD700"/>
    ${dots}
    <circle cx="111" cy="95" r="4" fill="#000"/><circle cx="129" cy="95" r="4" fill="#000"/>
    <path d="M110 106 Q120 116 130 106" stroke="#000" stroke-width="3" fill="none" stroke-linecap="round"/>`);
};

const drawElephant = () => wrap(`
    <rect width="240" height="240" fill="#B3E5FC"/>
    <rect y="185" width="240" height="55" fill="#A5D6A7"/>
    <ellipse cx="130" cy="160" rx="70" ry="45" fill="#9E9E9E"/>
    <rect x="80" y="180" width="20" height="30" rx="8" fill="#9E9E9E"/>
    <rect x="160" y="180" width="20" height="30" rx="8" fill="#9E9E9E"/>
    <ellipse cx="62" cy="100" rx="34" ry="42" fill="#BDBDBD"/>
    <ellipse cx="148" cy="100" rx="34" ry="42" fill="#BDBDBD"/>
    <ellipse cx="62" cy="100" rx="20" ry="27" fill="#FF8A80"/>
    <ellipse cx="148" cy="100" rx="20" ry="27" fill="#FF8A80"/>
    <ellipse cx="105" cy="100" rx="45" ry="42" fill="#9E9E9E"/>
    <path d="M95 125 Q92 160 100 180 Q106 192 116 184" stroke="#9E9E9E" stroke-width="18" fill="none" stroke-linecap="round"/>
    <circle cx="88" cy="92" r="9" fill="#fff"/><circle cx="122" cy="92" r="9" fill="#fff"/>
    <circle cx="89" cy="94" r="5" fill="#000"/><circle cx="123" cy="94" r="5" fill="#000"/>
    <circle cx="91" cy="92" r="1.8" fill="#fff"/><circle cx="125" cy="92" r="1.8" fill="#fff"/>
    <path d="M114 116 Q122 122 130 114" stroke="#424242" stroke-width="3" fill="none" stroke-linecap="round"/>`);

const drawHouse = () => {
    let grass = '';
    for (let i = 0; i < 7; i++) grass += `<rect x="${12 + i * 34}" y="${198 + (i % 2) * 4}" width="6" height="14" rx="2" fill="#388E3C"/>`;
    return wrap(`
    <rect width="240" height="240" fill="#B3E5FC"/>
    <rect y="190" width="240" height="50" fill="#81C784"/>
    <circle cx="36" cy="40" r="14" fill="#fff"/><circle cx="54" cy="34" r="18" fill="#fff"/><circle cx="74" cy="42" r="13" fill="#fff"/>
    <polygon points="120,40 40,110 200,110" fill="#E53935"/>
    <rect x="55" y="110" width="130" height="85" fill="#FFEB3B"/>
    <rect x="68" y="124" width="32" height="32" fill="#42A5F5"/>
    <line x1="84" y1="124" x2="84" y2="156" stroke="#fff" stroke-width="3"/><line x1="68" y1="140" x2="100" y2="140" stroke="#fff" stroke-width="3"/>
    <rect x="140" y="124" width="32" height="32" fill="#42A5F5"/>
    <line x1="156" y1="124" x2="156" y2="156" stroke="#fff" stroke-width="3"/><line x1="140" y1="140" x2="172" y2="140" stroke="#fff" stroke-width="3"/>
    <rect x="106" y="145" width="28" height="50" fill="#795548"/>
    <circle cx="128" cy="172" r="3" fill="#FFD700"/>
    ${grass}`);
};

const drawGarden = () => {
    const flowerColors = ['#E53935', '#EC407A', '#FDD835', '#E53935', '#EC407A'];
    const flowerX = [22, 50, 190, 214, 170];
    let flowers = '';
    flowerX.forEach((x, i) => {
        flowers += `<rect x="${x - 2}" y="${188 - (i % 2) * 8}" width="4" height="${30 + (i % 2) * 8}" fill="#2E7D32"/>
        <circle cx="${x}" cy="${184 - (i % 2) * 8}" r="9" fill="${flowerColors[i]}"/><circle cx="${x}" cy="${184 - (i % 2) * 8}" r="3" fill="#FFF59D"/>`;
    });
    const daisy = (x, y) => `<circle cx="${x}" cy="${y}" r="7" fill="#FFEB3B"/><circle cx="${x}" cy="${y}" r="2.5" fill="#F57F17"/>`;
    return wrap(`
    <rect width="240" height="240" fill="#E3F2FD"/>
    <g fill="#fff">
      <circle cx="30" cy="36" r="12"/><circle cx="46" cy="30" r="15"/><circle cx="62" cy="38" r="11"/>
      <circle cx="110" cy="22" r="10"/><circle cx="124" cy="18" r="13"/><circle cx="138" cy="24" r="9"/>
      <circle cx="180" cy="44" r="11"/><circle cx="196" cy="38" r="14"/><circle cx="212" cy="46" r="10"/>
    </g>
    <rect y="160" width="240" height="80" fill="#66BB6A"/>
    <rect x="108" y="110" width="24" height="80" rx="10" fill="#795548"/>
    <circle cx="96" cy="100" r="32" fill="#388E3C"/>
    <circle cx="144" cy="100" r="32" fill="#43A047"/>
    <circle cx="120" cy="76" r="34" fill="#4CAF50"/>
    <rect x="99" y="92" width="2" height="6" fill="#5D4037"/><circle cx="100" cy="102" r="7" fill="#E53935"/>
    <rect x="143" y="80" width="2" height="6" fill="#5D4037"/><circle cx="144" cy="90" r="7" fill="#E53935"/>
    ${flowers}
    ${daisy(80, 215)}${daisy(140, 222)}${daisy(200, 212)}
    <path d="M14 70 Q22 62 30 70 Q38 62 46 70" stroke="#37474F" stroke-width="2.5" fill="none"/>
    <ellipse cx="30" cy="72" rx="5" ry="2.5" fill="#37474F"/>
    <path d="M196 76 Q204 68 212 76 Q220 68 228 76" stroke="#37474F" stroke-width="2.5" fill="none"/>
    <ellipse cx="212" cy="78" rx="5" ry="2.5" fill="#37474F"/>`);
};

const toDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const drawRainbow = () => {
    const bands = ['#E53935', '#FB8C00', '#FDD835', '#43A047', '#1E88E5', '#8E24AA'];
    let arcs = '';
    bands.forEach((c, i) => {
        const r = 96 - i * 12;
        arcs += `<path d="M ${120 - r} 176 A ${r} ${r} 0 0 1 ${120 + r} 176" stroke="${c}" stroke-width="12" fill="none"/>`;
    });
    return wrap(`
    <rect width="240" height="240" fill="#E1F5FE"/>
    <circle cx="206" cy="36" r="18" fill="#FFD54F"/>
    <rect y="176" width="240" height="64" fill="#81C784"/>
    ${arcs}
    <circle cx="26" cy="176" r="16" fill="#fff"/><circle cx="44" cy="168" r="18" fill="#fff"/><circle cx="60" cy="178" r="13" fill="#fff"/>
    <circle cx="180" cy="178" r="13" fill="#fff"/><circle cx="196" cy="168" r="18" fill="#fff"/><circle cx="214" cy="176" r="16" fill="#fff"/>
    <circle cx="40" cy="214" r="6" fill="#F06292"/><circle cx="120" cy="222" r="6" fill="#FFEB3B"/><circle cx="200" cy="212" r="6" fill="#BA68C8"/>`);
};

const drawFish = () => wrap(`
    <rect width="240" height="240" fill="#4FC3F7"/>
    <rect y="200" width="240" height="40" fill="#FFE082"/>
    <path d="M30 200 Q22 170 34 150 Q24 130 34 110" stroke="#2E7D32" stroke-width="7" fill="none" stroke-linecap="round"/>
    <path d="M206 200 Q214 176 204 156 Q214 140 206 124" stroke="#43A047" stroke-width="7" fill="none" stroke-linecap="round"/>
    <polygon points="170,112 214,82 214,142" fill="#FB8C00"/>
    <ellipse cx="120" cy="112" rx="62" ry="42" fill="#FFA726"/>
    <path d="M100 74 Q120 52 144 74" fill="#FB8C00"/>
    <path d="M104 150 Q118 168 134 150" fill="#FB8C00"/>
    <path d="M118 74 Q108 112 118 150" stroke="#fff" stroke-width="8" fill="none"/>
    <path d="M146 80 Q138 112 146 144" stroke="#fff" stroke-width="7" fill="none"/>
    <circle cx="84" cy="102" r="11" fill="#fff"/><circle cx="82" cy="102" r="6" fill="#000"/><circle cx="80" cy="99" r="2" fill="#fff"/>
    <path d="M64 122 Q72 130 80 122" stroke="#000" stroke-width="3" fill="none" stroke-linecap="round"/>
    <circle cx="56" cy="70" r="6" fill="none" stroke="#fff" stroke-width="2"/><circle cx="46" cy="50" r="4" fill="none" stroke="#fff" stroke-width="2"/><circle cx="52" cy="32" r="3" fill="none" stroke="#fff" stroke-width="2"/>
    <circle cx="160" cy="214" r="5" fill="#F48FB1"/><circle cx="80" cy="218" r="4" fill="#fff"/>`);

const drawCat = () => wrap(`
    <rect width="240" height="240" fill="#FFF3E0"/>
    <rect y="190" width="240" height="50" fill="#BCAAA4"/>
    <path d="M170 176 Q214 170 206 120 Q202 100 214 92" stroke="#FF9800" stroke-width="14" fill="none" stroke-linecap="round"/>
    <ellipse cx="128" cy="160" rx="58" ry="40" fill="#FFA726"/>
    <ellipse cx="96" cy="196" rx="14" ry="9" fill="#FFA726"/><ellipse cx="150" cy="196" rx="14" ry="9" fill="#FFA726"/>
    <polygon points="68,70 78,26 104,58" fill="#FFA726"/><polygon points="152,58 176,26 184,70" fill="#FFA726"/>
    <polygon points="76,62 82,38 98,58" fill="#FFCCBC"/><polygon points="158,58 172,38 178,62" fill="#FFCCBC"/>
    <circle cx="126" cy="90" r="50" fill="#FFB74D"/>
    <ellipse cx="106" cy="84" rx="8" ry="11" fill="#2E7D32"/><ellipse cx="146" cy="84" rx="8" ry="11" fill="#2E7D32"/>
    <ellipse cx="106" cy="86" rx="3" ry="8" fill="#000"/><ellipse cx="146" cy="86" rx="3" ry="8" fill="#000"/>
    <polygon points="120,104 132,104 126,112" fill="#E91E63"/>
    <path d="M114 118 Q126 126 138 118" stroke="#5D4037" stroke-width="3" fill="none" stroke-linecap="round"/>
    <line x1="72" y1="104" x2="102" y2="108" stroke="#5D4037" stroke-width="2"/><line x1="72" y1="116" x2="102" y2="114" stroke="#5D4037" stroke-width="2"/>
    <line x1="150" y1="108" x2="180" y2="104" stroke="#5D4037" stroke-width="2"/><line x1="150" y1="114" x2="180" y2="116" stroke="#5D4037" stroke-width="2"/>`);

const drawCar = () => wrap(`
    <rect width="240" height="240" fill="#B3E5FC"/>
    <circle cx="40" cy="40" r="20" fill="#FFD54F"/>
    <circle cx="160" cy="36" r="12" fill="#fff"/><circle cx="176" cy="30" r="16" fill="#fff"/><circle cx="194" cy="38" r="12" fill="#fff"/>
    <rect y="120" width="240" height="40" fill="#81C784"/>
    <rect y="160" width="240" height="80" fill="#616161"/>
    <rect x="16" y="196" width="36" height="8" fill="#FFF"/><rect x="102" y="196" width="36" height="8" fill="#FFF"/><rect x="188" y="196" width="36" height="8" fill="#FFF"/>
    <polygon points="66,120 86,88 160,88 184,120" fill="#E53935"/>
    <polygon points="80,118 94,96 120,96 120,118" fill="#B3E5FC"/><polygon points="126,118 126,96 154,96 170,118" fill="#B3E5FC"/>
    <rect x="30" y="116" width="182" height="46" rx="16" fill="#F44336"/>
    <rect x="200" y="126" width="12" height="10" rx="3" fill="#FFEB3B"/><rect x="30" y="128" width="10" height="10" rx="3" fill="#FF7043"/>
    <line x1="120" y1="122" x2="120" y2="156" stroke="#C62828" stroke-width="3"/>
    <circle cx="72" cy="164" r="20" fill="#212121"/><circle cx="72" cy="164" r="8" fill="#BDBDBD"/>
    <circle cx="172" cy="164" r="20" fill="#212121"/><circle cx="172" cy="164" r="8" fill="#BDBDBD"/>`);

const drawButterfly = () => wrap(`
    <rect width="240" height="240" fill="#F1F8E9"/>
    <rect y="196" width="240" height="44" fill="#9CCC65"/>
    <ellipse cx="80" cy="84" rx="50" ry="44" fill="#AB47BC"/><ellipse cx="160" cy="84" rx="50" ry="44" fill="#AB47BC"/>
    <ellipse cx="88" cy="150" rx="36" ry="32" fill="#EC407A"/><ellipse cx="152" cy="150" rx="36" ry="32" fill="#EC407A"/>
    <circle cx="72" cy="80" r="16" fill="#FFEB3B"/><circle cx="168" cy="80" r="16" fill="#FFEB3B"/>
    <circle cx="86" cy="152" r="11" fill="#FFF59D"/><circle cx="154" cy="152" r="11" fill="#FFF59D"/>
    <ellipse cx="120" cy="120" rx="10" ry="56" fill="#4E342E"/>
    <circle cx="120" cy="62" r="12" fill="#4E342E"/>
    <path d="M114 54 Q100 28 88 24" stroke="#4E342E" stroke-width="3" fill="none"/><path d="M126 54 Q140 28 152 24" stroke="#4E342E" stroke-width="3" fill="none"/>
    <circle cx="88" cy="24" r="5" fill="#4E342E"/><circle cx="152" cy="24" r="5" fill="#4E342E"/>
    <circle cx="30" cy="206" r="9" fill="#FF7043"/><circle cx="30" cy="206" r="3" fill="#FFEB3B"/>
    <circle cx="210" cy="210" r="9" fill="#42A5F5"/><circle cx="210" cy="210" r="3" fill="#FFEB3B"/>`);

const drawRocket = () => {
    let stars = '';
    [[20, 30], [60, 70], [30, 150], [200, 60], [220, 140], [180, 200], [40, 210], [100, 24], [150, 110]].forEach(([x, y]) => {
        stars += `<circle cx="${x}" cy="${y}" r="2.5" fill="#FFF59D"/>`;
    });
    return wrap(`
    <rect width="240" height="240" fill="#1A237E"/>
    ${stars}
    <circle cx="196" cy="40" r="22" fill="#FFF9C4"/><circle cx="206" cy="34" r="20" fill="#1A237E"/>
    <polygon points="100,190 140,190 130,230 120,212 110,230" fill="#FF9800"/>
    <polygon points="108,190 132,190 124,214 120,204 116,214" fill="#FFEB3B"/>
    <polygon points="92,150 70,196 100,186" fill="#E53935"/><polygon points="148,150 170,196 140,186" fill="#E53935"/>
    <rect x="92" y="80" width="56" height="112" rx="14" fill="#ECEFF1"/>
    <path d="M92 90 Q120 20 148 90 Z" fill="#E53935"/>
    <circle cx="120" cy="116" r="16" fill="#90CAF9" stroke="#607D8B" stroke-width="5"/>
    <rect x="104" y="150" width="32" height="8" rx="3" fill="#E53935"/>`);
};

const drawBoat = () => wrap(`
    <rect width="240" height="240" fill="#E1F5FE"/>
    <circle cx="200" cy="40" r="20" fill="#FFD54F"/>
    <path d="M30 40 Q36 34 42 40 Q48 34 54 40" stroke="#37474F" stroke-width="2.5" fill="none"/>
    <path d="M70 60 Q76 54 82 60 Q88 54 94 60" stroke="#37474F" stroke-width="2.5" fill="none"/>
    <rect y="160" width="240" height="80" fill="#1E88E5"/>
    <rect x="116" y="40" width="7" height="120" fill="#6D4C41"/>
    <polygon points="112,46 112,146 48,146" fill="#FFFFFF"/>
    <polygon points="127,56 127,146 184,146" fill="#FFCA28"/>
    <polygon points="123,40 150,48 123,56" fill="#E53935"/>
    <polygon points="36,152 204,152 180,186 60,186" fill="#8D6E63"/>
    <rect x="36" y="150" width="168" height="8" fill="#5D4037"/>
    <circle cx="90" cy="168" r="6" fill="#FFF"/><circle cx="120" cy="168" r="6" fill="#FFF"/><circle cx="150" cy="168" r="6" fill="#FFF"/>
    <path d="M0 196 Q20 186 40 196 T80 196 T120 196 T160 196 T200 196 T240 196" stroke="#90CAF9" stroke-width="5" fill="none"/>
    <path d="M0 222 Q20 212 40 222 T80 222 T120 222 T160 222 T200 222 T240 222" stroke="#90CAF9" stroke-width="5" fill="none"/>`);

const drawCupcake = () => {
    const colors = ['#E53935', '#43A047', '#1E88E5', '#FDD835', '#8E24AA', '#FB8C00'];
    let sprinkles = '';
    [[92, 96], [118, 78], [146, 94], [104, 118], [134, 116], [160, 118], [78, 118], [124, 100]].forEach(([x, y], i) => {
        sprinkles += `<rect x="${x}" y="${y}" width="10" height="4" rx="2" fill="${colors[i % colors.length]}" transform="rotate(${(i * 40) % 180} ${x + 5} ${y + 2})"/>`;
    });
    return wrap(`
    <rect width="240" height="240" fill="#FCE4EC"/>
    <rect y="206" width="240" height="34" fill="#F8BBD0"/>
    <polygon points="62,136 178,136 160,210 80,210" fill="#29B6F6"/>
    <line x1="90" y1="136" x2="98" y2="210" stroke="#0288D1" stroke-width="4"/>
    <line x1="120" y1="136" x2="120" y2="210" stroke="#0288D1" stroke-width="4"/>
    <line x1="150" y1="136" x2="142" y2="210" stroke="#0288D1" stroke-width="4"/>
    <circle cx="74" cy="126" r="22" fill="#F48FB1"/><circle cx="166" cy="126" r="22" fill="#F48FB1"/>
    <circle cx="96" cy="110" r="28" fill="#F06292"/><circle cx="144" cy="110" r="28" fill="#F06292"/>
    <circle cx="120" cy="88" r="32" fill="#F48FB1"/>
    ${sprinkles}
    <path d="M122 58 Q128 36 142 32" stroke="#2E7D32" stroke-width="3" fill="none"/>
    <circle cx="120" cy="56" r="12" fill="#D50000"/><circle cx="116" cy="52" r="3" fill="#fff"/>`);
};

const drawTurtle = () => {
    let spots = '';
    [[120, 110], [92, 124], [148, 124], [106, 92], [134, 92], [120, 138]].forEach(([x, y]) => {
        spots += `<circle cx="${x}" cy="${y}" r="11" fill="#9CCC65" stroke="#33691E" stroke-width="3"/>`;
    });
    return wrap(`
    <rect width="240" height="240" fill="#B3E5FC"/>
    <circle cx="36" cy="36" r="18" fill="#FFD54F"/>
    <rect y="100" width="240" height="40" fill="#29B6F6"/>
    <rect y="140" width="240" height="100" fill="#FFE082"/>
    <ellipse cx="78" cy="158" rx="16" ry="11" fill="#8BC34A"/><ellipse cx="162" cy="158" rx="16" ry="11" fill="#8BC34A"/>
    <ellipse cx="74" cy="96" rx="16" ry="11" fill="#8BC34A"/><ellipse cx="166" cy="96" rx="16" ry="11" fill="#8BC34A"/>
    <ellipse cx="120" cy="166" rx="8" ry="10" fill="#8BC34A"/>
    <ellipse cx="190" cy="120" rx="24" ry="18" fill="#8BC34A"/>
    <circle cx="198" cy="114" r="4" fill="#000"/>
    <path d="M196 126 Q204 130 210 124" stroke="#33691E" stroke-width="2.5" fill="none" stroke-linecap="round"/>
    <ellipse cx="120" cy="120" rx="58" ry="44" fill="#558B2F"/>
    ${spots}
    <circle cx="40" cy="206" r="6" fill="#FF8A65"/><circle cx="206" cy="214" r="5" fill="#fff"/>
    <polygon points="30,226 38,214 46,226" fill="#F48FB1"/>`);
};

const drawCastle = () => wrap(`
    <rect width="240" height="240" fill="#E3F2FD"/>
    <circle cx="40" cy="36" r="12" fill="#fff"/><circle cx="56" cy="30" r="16" fill="#fff"/><circle cx="72" cy="38" r="11" fill="#fff"/>
    <rect y="196" width="240" height="44" fill="#66BB6A"/>
    <rect x="52" y="110" width="136" height="90" fill="#B0BEC5"/>
    <rect x="52" y="98" width="16" height="14" fill="#B0BEC5"/><rect x="80" y="98" width="16" height="14" fill="#B0BEC5"/>
    <rect x="112" y="98" width="16" height="14" fill="#B0BEC5"/><rect x="144" y="98" width="16" height="14" fill="#B0BEC5"/><rect x="172" y="98" width="16" height="14" fill="#B0BEC5"/>
    <rect x="20" y="80" width="40" height="120" fill="#90A4AE"/><rect x="180" y="80" width="40" height="120" fill="#90A4AE"/>
    <polygon points="14,82 40,30 66,82" fill="#7E57C2"/><polygon points="174,82 200,30 226,82" fill="#7E57C2"/>
    <rect x="38" y="14" width="3" height="18" fill="#5D4037"/><polygon points="41,14 58,20 41,26" fill="#E53935"/>
    <rect x="198" y="14" width="3" height="18" fill="#5D4037"/><polygon points="201,14 218,20 201,26" fill="#FDD835"/>
    <path d="M100 200 L100 162 Q120 136 140 162 L140 200 Z" fill="#6D4C41"/>
    <circle cx="132" cy="178" r="3" fill="#FFD54F"/>
    <rect x="32" y="110" width="16" height="22" rx="8" fill="#37474F"/><rect x="192" y="110" width="16" height="22" rx="8" fill="#37474F"/>
    <rect x="72" y="130" width="16" height="20" rx="8" fill="#37474F"/><rect x="152" y="130" width="16" height="20" rx="8" fill="#37474F"/>`);

const EXTRA_LEVELS = [
    { id: 6, name: 'Over the Rainbow', icon: '🌈', rows: 2, cols: 2, age: 'Age 4', minAge: 4, stars: 3, draw: drawRainbow, success: 'made a big rainbow!' },
    { id: 7, name: 'Happy Fish', icon: '🐠', rows: 2, cols: 2, age: 'Age 4', minAge: 4, stars: 3, draw: drawFish, success: 'helped the fish swim!' },
    { id: 8, name: 'Little Kitty', icon: '🐱', rows: 2, cols: 3, age: 'Age 4–5', minAge: 4.5, stars: 4, draw: drawCat, success: 'found the little kitty!' },
    { id: 9, name: 'Zoom Zoom Car', icon: '🚗', rows: 2, cols: 3, age: 'Age 4–5', minAge: 4.5, stars: 4, draw: drawCar, success: 'fixed the red car!' },
    { id: 10, name: 'Flutter Butterfly', icon: '🦋', rows: 3, cols: 2, age: 'Age 4–5', minAge: 4.5, stars: 4, draw: drawButterfly, success: 'made the butterfly fly!' },
    { id: 11, name: 'Space Rocket', icon: '🚀', rows: 3, cols: 3, age: 'Age 5', minAge: 5, stars: 5, draw: drawRocket, success: 'launched the rocket!' },
    { id: 12, name: 'Sailing Boat', icon: '⛵', rows: 3, cols: 3, age: 'Age 5', minAge: 5, stars: 5, draw: drawBoat, success: 'built the sailing boat!' },
    { id: 13, name: 'Yummy Cupcake', icon: '🧁', rows: 3, cols: 3, age: 'Age 5', minAge: 5, stars: 5, draw: drawCupcake, success: 'baked a yummy cupcake!' },
    { id: 14, name: 'Beach Turtle', icon: '🐢', rows: 3, cols: 4, age: 'Age 5+', minAge: 5.5, stars: 5, draw: drawTurtle, success: 'found the beach turtle!' },
    { id: 15, name: 'Royal Castle', icon: '🏰', rows: 4, cols: 4, age: 'Age 5+', minAge: 5.5, stars: 5, draw: drawCastle, success: 'built the royal castle!' },
];

const LEVELS = [
    { id: 1, name: 'The Big Sun', icon: '☀️', rows: 1, cols: 2, age: 'Age 3', minAge: 3, stars: 1, draw: drawSun, success: 'put the sun together!' },
    { id: 2, name: 'A Pretty Flower', icon: '🌸', rows: 1, cols: 3, age: 'Age 3–4', minAge: 3.5, stars: 2, draw: drawFlower, success: 'made the pretty flower!' },
    { id: 3, name: 'Baby Elephant', icon: '🐘', rows: 2, cols: 2, age: 'Age 4', minAge: 4, stars: 3, draw: drawElephant, success: 'found the baby elephant!' },
    { id: 4, name: 'My Little House', icon: '🏠', rows: 2, cols: 3, age: 'Age 4–5', minAge: 4.5, stars: 4, draw: drawHouse, success: 'built the little house!' },
    { id: 5, name: 'The Magic Garden', icon: '🌳', rows: 3, cols: 3, age: 'Age 5', minAge: 5, stars: 5, draw: drawGarden, success: 'grew the magic garden!' },
    ...EXTRA_LEVELS,
];

export const PUZZLE_LEVEL_COUNT = LEVELS.length;

// Level-card colors by difficulty (1-5 stars): soft fill + chunky edge.
const CARD_THEMES = [
    { bg: '#FFF3C4', edge: '#FFC107' },
    { bg: '#FFE0EC', edge: '#F48FB1' },
    { bg: '#DCEFFF', edge: '#64B5F6' },
    { bg: '#E3F6E5', edge: '#81C784' },
    { bg: '#F1E4FA', edge: '#BA68C8' },
];

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const loadCompleted = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch (e) {
        return new Set();
    }
};

const PuzzleGame = ({ childAge, childName, onComplete }) => {
    const [view, setView] = useState('select');
    const [currentLevel, setCurrentLevel] = useState(null);
    const [pieces, setPieces] = useState([]);
    const [slots, setSlots] = useState([]);
    const [dragging, setDragging] = useState(null); // {pieceId, k (board scale), offsetX, offsetY, x, y}
    const [placedCount, setPlacedCount] = useState(0);
    const [completedLevels, setCompletedLevels] = useState(loadCompleted);
    const [isMobile, setIsMobile] = useState(false);
    const [shakeId, setShakeId] = useState(null);
    const [flashSlot, setFlashSlot] = useState(null);
    const [phrase, setPhrase] = useState(PHRASES[0]);

    const audioCtx = useRef(null);
    const wrapperRef = useRef(null);
    const boardRef = useRef(null);
    const startTime = useRef(0);
    const wrongMoves = useRef(0);
    const placedRef = useRef(0);
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    // What the Replay button says for the current screen.
    useEffect(() => {
        setReplay(view === 'select'
            ? 'Pick a puzzle! Tap a picture to start.'
            : view === 'complete'
                ? `${childName || 'You'} ${currentLevel?.success || 'did it!'}`
                : `Drag the pieces to make ${currentLevel?.name}. Look at the picture to help you!`);
        return () => setReplay(null);
    }, [view, currentLevel, childName]);
    useEffect(() => {
        setHud(view === 'select' || !currentLevel
            ? {
                scoreLabel: 'Done',
                score: LEVELS.filter((l) => completedLevels.has(l.id)).length,
                total: LEVELS.length,
                question: '🧩 Pick a puzzle!',
            }
            : {
                scoreLabel: 'Pieces',
                score: placedCount,
                total: pieces.length,
                questionLabel: 'Make:',
                question: `${currentLevel.icon} ${currentLevel.name}`,
            });
        return () => setHud(null);
    }, [view, currentLevel, completedLevels, placedCount, pieces.length]);
    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

    // ─── Responsive ─────────────────────────────────────────────────────────
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            setIsMobile(entries[0].contentRect.width < 520);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // ─── Audio ──────────────────────────────────────────────────────────────
    const getCtx = () => {
        try {
            if (!audioCtx.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return null;
                audioCtx.current = new Ctx();
            }
            if (audioCtx.current.state === 'suspended') audioCtx.current.resume();
            return audioCtx.current;
        } catch (e) {
            return null;
        }
    };
    const tone = (freq, delayMs, gain, dur, type = 'sine') => {
        const vol = getVolume();
        if (!vol) return;
        try {
            const ctx = getCtx();
            if (!ctx) return;
            const t = ctx.currentTime + delayMs / 1000;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = type;
            osc.frequency.value = freq;
            g.gain.setValueAtTime(gain * vol, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + dur + 0.02);
        } catch (e) { /* audio must never block the game */ }
    };
    const playSuccess = () => [523, 659, 784].forEach((f, i) => tone(f, i * 120, 0.18, 0.15));
    const playWrong = () => tone(220, 0, 0.12, 0.18, 'square');
    const playComplete = () => [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 120, 0.2, 0.22));

    // ─── Level lifecycle ────────────────────────────────────────────────────
    const isUnlocked = (level) => {
        if (level.id === 1) return true;
        if (typeof childAge === 'number' && level.minAge <= childAge) return true;
        return completedLevels.has(level.id - 1) || completedLevels.has(level.id);
    };

    const startLevel = (level) => {
        const list = [];
        for (let r = 0; r < level.rows; r++) {
            for (let c = 0; c < level.cols; c++) list.push({ id: `${r}-${c}`, row: r, col: c, placed: false });
        }
        setPieces(shuffle(list).map((p, i) => ({ ...p, trayIndex: i })));
        setSlots(list.map((p) => ({ row: p.row, col: p.col, filled: false, hover: false })));
        setPlacedCount(0);
        placedRef.current = 0;
        timers.current.forEach(clearTimeout);
        timers.current = [];
        setDragging(null);
        setCurrentLevel({ ...level, url: toDataUrl(level.draw()) });
        startTime.current = Date.now();
        wrongMoves.current = 0;
        setView('play');
        speakText(`Drag the pieces to make ${level.name}!`);
    };

    const finishLevel = (level) => {
        playComplete();
        speakText(`${childName || 'You'} ${level.success}`);
        setPhrase(PHRASES[Math.floor(Math.random() * PHRASES.length)]);
        setCompletedLevels((prev) => {
            const next = new Set(prev);
            next.add(level.id);
            try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch (e) { /* ignore */ }
            return next;
        });
        setView('complete');
        onComplete?.({
            levelId: level.id,
            levelName: level.name,
            pieces: level.rows * level.cols,
            timeSeconds: Math.round((Date.now() - startTime.current) / 1000),
            wrongMoves: wrongMoves.current,
            completedAt: new Date().toISOString(),
        });
    };

    // ─── Drag & drop ────────────────────────────────────────────────────────
    const pieceW = currentLevel ? BOARD / currentLevel.cols : 0;
    const pieceH = currentLevel ? BOARD / currentLevel.rows : 0;

    const slotCenters = useCallback(() => {
        const board = boardRef.current;
        if (!board || !currentLevel) return [];
        const rect = board.getBoundingClientRect();
        // The board may be scaled down to fit the screen (GameFitBox).
        const k = rect.width / (board.offsetWidth || rect.width) || 1;
        return slots.map((s, i) => ({
            index: i,
            row: s.row,
            col: s.col,
            filled: s.filled,
            x: rect.left + (s.col * pieceW + pieceW / 2) * k,
            y: rect.top + (s.row * pieceH + pieceH / 2) * k,
            k,
        }));
    }, [slots, currentLevel, pieceW, pieceH]);

    const closestSlot = (cx, cy) => {
        let best = null;
        slotCenters().forEach((s) => {
            if (s.filled) return;
            const d = Math.hypot(s.x - cx, s.y - cy);
            if (d <= SNAP_DISTANCE * s.k && (!best || d < best.d)) best = { ...s, d };
        });
        return best;
    };

    const onPointerDown = (e, piece) => {
        if (piece.placed || view !== 'play') return;
        e.preventDefault();
        getCtx();
        const rect = e.currentTarget.getBoundingClientRect();
        // On-screen scale of the board (GameFitBox); the dragged ghost is
        // drawn outside the board, so it is scaled to match.
        const k = rect.width / (e.currentTarget.offsetWidth || rect.width) || 1;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        setDragging({
            pieceId: piece.id,
            k,
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
            x: rect.left,
            y: rect.top,
            snapping: false,
        });
    };

    const onPointerMove = (e) => {
        if (!dragging || dragging.snapping) return;
        const x = e.clientX - dragging.offsetX;
        const y = e.clientY - dragging.offsetY;
        setDragging((d) => (d ? { ...d, x, y } : d));
        const piece = pieces.find((p) => p.id === dragging.pieceId);
        const hit = closestSlot(x + (pieceW / 2) * dragging.k, y + (pieceH / 2) * dragging.k);
        const correct = hit && hit.row === piece.row && hit.col === piece.col;
        setSlots((prev) => prev.map((s, i) => {
            const hover = !!correct && i === hit.index;
            return s.hover === hover ? s : { ...s, hover };
        }));
    };

    const onPointerUp = () => {
        if (!dragging || dragging.snapping) return;
        const piece = pieces.find((p) => p.id === dragging.pieceId);
        const hit = closestSlot(dragging.x + (pieceW / 2) * dragging.k, dragging.y + (pieceH / 2) * dragging.k);
        setSlots((prev) => prev.map((s) => (s.hover ? { ...s, hover: false } : s)));

        if (hit && hit.row === piece.row && hit.col === piece.col) {
            const boardRect = boardRef.current.getBoundingClientRect();
            // Glide the ghost onto the exact slot, then lock it in.
            setDragging((d) => ({ ...d, snapping: true, x: boardRect.left + hit.col * pieceW * d.k, y: boardRect.top + hit.row * pieceH * d.k }));
            later(() => {
                setDragging(null);
                setPieces((prev) => prev.map((p) => (p.id === piece.id ? { ...p, placed: true } : p)));
                setSlots((prev) => prev.map((s, i) => (i === hit.index ? { ...s, filled: true } : s)));
                setFlashSlot(hit.index);
                later(() => setFlashSlot(null), 600);
                playSuccess();
                const total = currentLevel.rows * currentLevel.cols;
                placedRef.current += 1;
                setPlacedCount(placedRef.current);
                if (placedRef.current >= total) {
                    const level = currentLevel;
                    later(() => finishLevel(level), 350);
                }
            }, 180);
        } else if (hit) {
            wrongMoves.current += 1;
            playWrong();
            setDragging(null);
            setShakeId(piece.id);
            later(() => setShakeId(null), 450);
        } else {
            setDragging(null);
        }
    };

    // ─── Render helpers ─────────────────────────────────────────────────────
    const pieceStyle = (piece, extra = {}) => ({
        width: pieceW,
        height: pieceH,
        backgroundImage: `url("${currentLevel.url}")`,
        backgroundSize: `${BOARD}px ${BOARD}px`,
        backgroundPosition: `-${piece.col * pieceW}px -${piece.row * pieceH}px`,
        borderRadius: 8,
        boxSizing: 'border-box',
        ...extra,
    });

    const styles = `
        @keyframes star-pop {
          0%   { transform: scale(0) rotate(-20deg); opacity: 0 }
          60%  { transform: scale(1.3) rotate(5deg) }
          100% { transform: scale(1) rotate(0); opacity: 1 }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0) }
          25%       { transform: translateX(-8px) }
          75%       { transform: translateX(8px) }
        }
        @keyframes snap-glow {
          0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.6) }
          50%  { box-shadow: 0 0 0 8px rgba(34,197,94,0) }
          100% { box-shadow: 0 0 0 0 rgba(34,197,94,0) }
        }
        @keyframes pz-slide-in {
          from { transform: translateY(20px); opacity: 0 }
          to   { transform: translateY(0); opacity: 1 }
        }
        .pz-piece { cursor: grab; transition: box-shadow .15s, transform .15s; touch-action: none; }
        .pz-piece:hover { box-shadow: 0 6px 14px rgba(0,0,0,.25); transform: translateY(-2px); }
        .pz-piece.dragging { cursor: grabbing; box-shadow: 0 12px 24px rgba(0,0,0,.3); }
        .pz-shake { animation: shake .4s ease; }
        /* 15 levels on one screen: 5x3 grid of compact horizontal tiles whose
           row height follows the viewport (modal body is 100vh - 170px). */
        .pz-grid {
            --pz-row: clamp(76px, calc((100vh - 400px) / 3), 118px);
            display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); grid-auto-rows: var(--pz-row); gap: 14px 12px;
        }
        .pz-card {
            position: relative; display: flex; align-items: center; gap: 10px; min-width: 0;
            padding: 6px 10px 6px 8px; border-radius: 20px; text-align: left; cursor: pointer;
            background: linear-gradient(160deg, #fff 0%, var(--pz-bg) 100%);
            border: 3px solid var(--pz-edge);
            box-shadow: 0 5px 0 var(--pz-edge), 0 8px 18px rgba(0,0,0,.08);
            transition: transform .18s ease, box-shadow .18s ease;
        }
        .pz-card:not(.locked):hover { transform: translateY(-4px) rotate(-1deg); box-shadow: 0 9px 0 var(--pz-edge), 0 14px 24px rgba(0,0,0,.12); }
        .pz-card:not(.locked):active { transform: translateY(2px); box-shadow: 0 2px 0 var(--pz-edge); }
        .pz-card.done { border-color: #22C55E; box-shadow: 0 5px 0 #16A34A, 0 8px 18px rgba(34,197,94,.18); }
        .pz-card.locked { cursor: not-allowed; background: #F1F5F9; border-color: #CBD5E1; box-shadow: 0 5px 0 #CBD5E1; }
        .pz-card.locked .pz-text { opacity: .55; }
        .pz-num {
            position: absolute; top: -10px; left: -8px; width: 24px; height: 24px; border-radius: 50%;
            background: #3e2723; color: #fff; font-weight: 900; font-size: 12px;
            display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.2);
        }
        .pz-done {
            position: absolute; top: -10px; right: -8px; width: 24px; height: 24px; border-radius: 50%;
            background: #22C55E; color: #fff; font-weight: 900; font-size: 13px; display: flex; align-items: center; justify-content: center;
            border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.2);
        }
        .pz-icon {
            flex-shrink: 0; width: min(56px, calc(var(--pz-row) - 20px)); aspect-ratio: 1; border-radius: 50%; background: #fff;
            font-size: min(32px, calc(var(--pz-row) * .34)); display: flex; align-items: center; justify-content: center;
            box-shadow: inset 0 -3px 0 rgba(0,0,0,.06), 0 3px 8px rgba(0,0,0,.08);
        }
        .pz-card.locked .pz-icon { background: #E2E8F0; font-size: 22px; }
        .pz-text { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .pz-name { font-weight: 900; color: #3e2723; font-size: 14px; line-height: 1.12; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .pz-meta { font-size: 11px; font-weight: 800; color: #64748B; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pz-stars { color: #F59E0B; letter-spacing: 1px; font-size: 12px; line-height: 1; }
        .pz-stars span { color: #E2E8F0; }
        .pz-overlay {
            position: absolute; inset: 0; z-index: 30; display: flex; align-items: center; justify-content: center;
            background: rgba(255,255,255,.82); border-radius: 18px; backdrop-filter: blur(2px);
        }
        @media (max-width: 900px) { .pz-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 560px) { .pz-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: 84px; } }
    `;

    const font = { fontFamily: '"Baloo 2", "Fredoka One", "Comic Sans MS", system-ui, sans-serif' };
    const muted = { fontSize: 12, color: '#64748B', marginTop: 6, textAlign: 'center' };

    // ─── Level select ───────────────────────────────────────────────────────
    const renderSelect = () => {
        return (
            <div style={{ padding: '4px 6px 8px' }}>
                <div className="pz-grid">
                    {LEVELS.map((level) => {
                        const unlocked = isUnlocked(level);
                        const done = completedLevels.has(level.id);
                        const theme = CARD_THEMES[level.stars - 1];
                        return (
                            <button
                                key={level.id}
                                type="button"
                                className={`pz-card ${unlocked ? '' : 'locked'} ${done ? 'done' : ''}`}
                                disabled={!unlocked}
                                onClick={() => unlocked && startLevel(level)}
                                style={{ '--pz-bg': theme.bg, '--pz-edge': theme.edge, ...font }}
                            >
                                <span className="pz-num">{level.id}</span>
                                {done && <span className="pz-done">✓</span>}
                                <div className="pz-icon">{unlocked ? level.icon : '🔒'}</div>
                                <div className="pz-text">
                                    <div className="pz-name" title={level.name}>{level.name}</div>
                                    <div className="pz-meta" title={`${level.rows * level.cols} pieces · ${level.age}`}>{unlocked ? `🧩 ${level.rows * level.cols} · ${level.age}` : `Finish level ${level.id - 1} first`}</div>
                                    <div className="pz-stars">{'★'.repeat(level.stars)}<span>{'★'.repeat(5 - level.stars)}</span></div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ─── Play ───────────────────────────────────────────────────────────────
    const renderPlay = () => {
        const total = currentLevel.rows * currentLevel.cols;
        const hintSize = isMobile ? 90 : 120;
        const trayPieces = pieces.filter((p) => !p.placed).sort((a, b) => a.trayIndex - b.trayIndex);

        const hint = (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ position: 'relative', padding: 6, background: '#fff', border: '3px solid #F59E0B', borderRadius: 14 }}>
                    <img src={currentLevel.url} alt="Hint" width={hintSize} height={hintSize} style={{ display: 'block', borderRadius: 10 }} draggable={false} />
                    <span style={{ position: 'absolute', top: -10, left: -10, background: '#F59E0B', color: '#fff', fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 10 }}>👁 Hint</span>
                </div>
                <div style={muted}>Look at the picture!</div>
            </div>
        );

        const board = (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div ref={boardRef} style={{ position: 'relative', width: BOARD, height: BOARD, background: '#F8FAFC', borderRadius: 12, boxShadow: 'inset 0 0 0 2px #E2E8F0' }}>
                    {slots.map((s, i) => {
                        const piece = pieces.find((p) => p.row === s.row && p.col === s.col);
                        return (
                            <div
                                key={i}
                                style={{
                                    position: 'absolute', left: s.col * pieceW, top: s.row * pieceH, width: pieceW, height: pieceH,
                                    boxSizing: 'border-box',
                                    border: s.filled ? 'none' : `2px dashed ${s.hover ? '#22C55E' : '#94A3B8'}`,
                                    background: s.hover ? 'rgba(34,197,94,0.18)' : 'transparent',
                                    boxShadow: s.hover ? '0 0 12px rgba(34,197,94,.7)' : 'none',
                                    borderRadius: 8, transition: 'background .15s, box-shadow .15s',
                                }}
                            >
                                {s.filled && piece && (
                                    <div style={pieceStyle(piece, {
                                        border: flashSlot === i ? '2px solid #22C55E' : '0',
                                        animation: flashSlot === i ? 'snap-glow .6s ease' : 'none',
                                        borderRadius: 0, pointerEvents: 'none',
                                    })} />
                                )}
                            </div>
                        );
                    })}
                </div>
                <div style={muted}>Put pieces here!</div>
            </div>
        );

        const tray = (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: isMobile ? 'auto' : 150 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', maxWidth: isMobile ? 300 : Math.max(260, currentLevel.cols * (pieceW + 10) + 16), minHeight: pieceH, padding: 8, background: '#F1F5F9', borderRadius: 12, boxSizing: 'border-box' }}>
                    {trayPieces.map((p) => {
                        const isDragged = dragging?.pieceId === p.id;
                        return (
                            <div
                                key={p.id}
                                className={`pz-piece ${shakeId === p.id ? 'pz-shake' : ''}`}
                                onPointerDown={(e) => onPointerDown(e, p)}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                onPointerCancel={onPointerUp}
                                style={pieceStyle(p, { border: '2px solid rgba(255,255,255,0.6)', opacity: isDragged ? 0.25 : 1 })}
                            />
                        );
                    })}
                    {trayPieces.length === 0 && <div style={{ color: '#94A3B8', fontSize: 13, alignSelf: 'center' }}>All done! 🎉</div>}
                </div>
                <div style={muted}>Pieces</div>
            </div>
        );

        return (
            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <button type="button" onClick={() => { setDragging(null); setView('select'); }} style={{ ...font, minHeight: 36, padding: '6px 14px', borderRadius: 10, border: '2px solid #CBD5E1', background: '#fff', fontWeight: 700, cursor: 'pointer' }}>← Back</button>
                    <div style={{ fontWeight: 800, color: '#1E293B', fontSize: 18 }}>{currentLevel.icon} {currentLevel.name}</div>
                    <div style={{ display: 'flex', gap: 5 }}>
                        {Array.from({ length: total }).map((_, i) => (
                            <span key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: i < placedCount ? '#22C55E' : '#E2E8F0', transition: 'background .2s' }} />
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'center', gap: isMobile ? 14 : 24 }}>
                    {hint}
                    {board}
                    {view === 'play' && tray}
                </div>
                {view === 'complete' && <div className="pz-overlay">{renderComplete()}</div>}
            </div>
        );
    };

    // ─── Complete ───────────────────────────────────────────────────────────
    const renderComplete = () => {
        const who = childName ? childName : 'You';
        const isLast = currentLevel.id === LEVELS.length;
        const btn = { ...font, minHeight: 44, padding: '8px 20px', borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: 'pointer' };
        return (
            <div style={{ width: 'min(420px, 92%)', padding: 20, borderRadius: 22, background: '#fff', border: '4px solid #22C55E', textAlign: 'center', boxShadow: '0 12px 32px rgba(0,0,0,.18)', animation: 'pz-slide-in .35s ease' }}>
                <div style={{ fontSize: 48 }}>🎉</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>{who} {currentLevel.success}</div>
                <div style={{ color: '#16A34A', fontWeight: 700, marginTop: 4 }}>{phrase}</div>
                <div style={{ fontSize: 30, color: '#F59E0B', margin: '8px 0 14px' }}>
                    {Array.from({ length: currentLevel.stars }).map((_, i) => (
                        <span key={i} style={{ display: 'inline-block', opacity: 0, animation: `star-pop .45s ease ${i * 150}ms forwards` }}>★</span>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => startLevel(currentLevel)} style={{ ...btn, background: '#fff', color: '#2563EB', border: '2px solid #2563EB' }}>Play again</button>
                    {!isLast && (
                        <button type="button" onClick={() => startLevel(LEVELS[currentLevel.id])} style={{ ...btn, background: '#2563EB', color: '#fff', border: '2px solid #2563EB' }}>Next puzzle</button>
                    )}
                </div>
            </div>
        );
    };

    const draggedPiece = dragging && pieces.find((p) => p.id === dragging.pieceId);

    return (
        <div ref={wrapperRef} style={{ ...font, width: '100%', padding: 12, boxSizing: 'border-box', userSelect: 'none' }}>
            <style>{styles}</style>
            {view === 'select' ? renderSelect() : renderPlay()}
            {draggedPiece && createPortal(
                <div
                    className="pz-piece dragging"
                    style={pieceStyle(draggedPiece, {
                        position: 'fixed', left: dragging.x, top: dragging.y, zIndex: 9999, pointerEvents: 'none',
                        transform: dragging.k && dragging.k !== 1 ? `scale(${dragging.k})` : undefined,
                        transformOrigin: '0 0',
                        border: '2px solid rgba(255,255,255,0.6)',
                        transition: dragging.snapping ? 'left 180ms ease, top 180ms ease' : 'none',
                    })}
                />,
                document.body
            )}
        </div>
    );
};

export default PuzzleGame;
